import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { SERVICES } from "../data/services";
import { Logo, Wordmark } from "./Logo";

const navLinkClass = ({ isActive }) =>
  `font-display text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
    isActive ? "text-brand" : "text-[#C9D2DE] hover:text-white"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/70 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="Sempresera home">
          <Logo className="h-14 w-14 md:h-16 md:w-16" />
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          <div className="group relative">
            <button
              type="button"
              data-testid="nav-services-trigger"
              className="flex items-center gap-1.5 py-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-[#C9D2DE] transition-colors duration-300 group-hover:text-white"
            >
              Services <ChevronDown size={14} />
            </button>
            <div className="invisible pointer-events-none absolute left-0 top-full pt-2 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100">
              <div className="glass w-80 bg-ink/95 p-2">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`nav-service-${s.slug}`}
                    className="block px-4 py-3 transition-colors duration-150 hover:bg-white/5"
                  >
                    <span className="block font-display text-sm font-bold text-white">{s.name}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-[#A8B4C2]">{s.short}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/pricing" className={navLinkClass} data-testid="nav-pricing-link">
            Pricing
          </NavLink>
          <NavLink to="/insights" className={navLinkClass} data-testid="nav-insights-link">
            Insights
          </NavLink>
          <NavLink to="/about" className={navLinkClass} data-testid="nav-about-link">
            About
          </NavLink>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact-link">
            Contact
          </NavLink>
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <Link to="/contact?mode=book" className="btn-primary !px-5 !py-2.5" data-testid="header-book-cta">
            Book a free consultation
          </Link>
        </div>

        <button
          type="button"
          className="text-white lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-x-0 top-16 z-40 h-[calc(100dvh-4rem)] overflow-y-auto border-t border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-1 px-5 py-6" aria-label="Mobile">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="py-2.5 font-display text-sm font-bold uppercase tracking-widest text-zinc-300" data-testid={`mobile-nav-service-${s.slug}`}>
                {s.name}
              </Link>
            ))}
            <Link to="/pricing" onClick={() => setOpen(false)} className="py-2.5 font-display text-sm font-bold uppercase tracking-widest text-zinc-300" data-testid="mobile-nav-pricing">Pricing</Link>
            <Link to="/insights" onClick={() => setOpen(false)} className="py-2.5 font-display text-sm font-bold uppercase tracking-widest text-zinc-300" data-testid="mobile-nav-insights">Insights</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="py-2.5 font-display text-sm font-bold uppercase tracking-widest text-zinc-300" data-testid="mobile-nav-about">About</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="py-2.5 font-display text-sm font-bold uppercase tracking-widest text-zinc-300" data-testid="mobile-nav-contact">Contact</Link>
            <Link to="/contact?mode=book" onClick={() => setOpen(false)} className="btn-primary mt-4" data-testid="mobile-book-cta">
              Book a free consultation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
